import { Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { RichTextEditor } from '../../../detail-form/RichTextEditor';
import type { ClientEmailSource } from './financeEmailComposeUtils';

export type FinanceEmailComposeFieldsProps = {
  cc: string;
  subject: string;
  message: string;
  onCcChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  disabled?: boolean;
  to?: string;
  onToChange?: (value: string) => void;
  /** Umbraco-configured BCC; shown read-only when provided (including empty). */
  bcc?: string;
  ccLabel?: string;
  ccHelperText?: string;
  bccHelperText?: string;
  financeEmail?: string;
  clientEmail?: string;
  emailSource?: ClientEmailSource | null;
  onEmailSourceChange?: (source: ClientEmailSource) => void;
};

export const FinanceEmailComposeFields = ({
  cc,
  subject,
  message,
  onCcChange,
  onSubjectChange,
  onMessageChange,
  disabled = false,
  to = '',
  onToChange,
  bcc,
  ccLabel = 'CC',
  ccHelperText = 'Separate multiple addresses with comma or semicolon',
  bccHelperText = 'Configured in Umbraco. Applied automatically on send.',
  financeEmail,
  clientEmail,
  emailSource,
  onEmailSourceChange,
}: FinanceEmailComposeFieldsProps) => {
  const showTo = Boolean(onToChange);
  const showEmailSource =
    showTo &&
    Boolean(onEmailSourceChange) &&
    (Boolean(financeEmail?.trim()) || Boolean(clientEmail?.trim()));

  return (
    <Stack spacing={2}>
      {showEmailSource && (
        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ mb: 0.75, color: disabled ? 'text.disabled' : 'text.primary' }}
          >
            Recipient email
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={emailSource}
            disabled={disabled}
            onChange={(_event, value: ClientEmailSource | null) => {
              if (value) {
                onEmailSourceChange?.(value);
              }
            }}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton
              value="finance"
              disabled={!financeEmail?.trim()}
              sx={{ textTransform: 'none' }}
            >
              Finance email
              {financeEmail?.trim() ? ` (${financeEmail.trim()})` : ''}
            </ToggleButton>
            <ToggleButton
              value="client"
              disabled={!clientEmail?.trim()}
              sx={{ textTransform: 'none' }}
            >
              Client email
              {clientEmail?.trim() ? ` (${clientEmail.trim()})` : ''}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {showTo && (
        <TextField
          label="To"
          value={to}
          onChange={(event) => onToChange?.(event.target.value)}
          fullWidth
          required
          disabled={disabled}
        />
      )}

      <TextField
        label={ccLabel}
        value={cc}
        onChange={(event) => onCcChange(event.target.value)}
        fullWidth
        disabled={disabled}
        helperText={ccHelperText}
      />

      {bcc !== undefined ? (
        <TextField
          label="BCC"
          value={bcc}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled={disabled}
          helperText={bccHelperText}
        />
      ) : null}

      <TextField
        label="Subject"
        value={subject}
        onChange={(event) => onSubjectChange(event.target.value)}
        fullWidth
        required
        disabled={disabled}
      />

      <Box>
        <RichTextEditor
          value={message}
          onChange={onMessageChange}
          label="Message"
          readOnly={disabled}
          trimTrailingEmptyParagraph
        />
      </Box>
    </Stack>
  );
};
